import { makeAutoObservable, runInAction } from "mobx";
import apiHandler from "../handlers/ApiHandler";
import authStore from "./AuthStore";

class DeviceNoteStore {
  notes = [];
  loading = false;
  error = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchNotes() {
    this.setLoading(true);
    try {
      const response = await apiHandler.get("/api/device-notes");
      runInAction(() => {
        this.notes = response; // adjust if response is wrapped in .data
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  async addNote(noteData) {
    this.setLoading(true);
    try {
      const currentUserId = authStore.user?.id;
      if (!currentUserId) throw new Error("No authenticated user");

      const payload = {
        ...noteData,
        created_by: currentUserId,
      };

      const response = await apiHandler.post("/api/device-notes", payload);
      runInAction(() => {
        this.notes.push(response);
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  async updateNote(noteId, noteData) {
    this.setLoading(true);
    try {
      const response = await apiHandler.put(
        `/api/device-notes/${noteId}`,
        noteData
      );
      runInAction(() => {
        this.notes = this.notes.map((note) =>
          note.id === noteId ? response?.data || response : note
        );
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  async archiveNote(noteId) {
    this.setLoading(true);
    try {
      const deletedAt = new Date().toISOString();
      await apiHandler.patch(`/api/device-notes/${noteId}/archive`, {
        deleted_at: deletedAt,
      });

      runInAction(() => {
        this.notes = this.notes.map((note) =>
          note.id === noteId ? { ...note, deleted_at: deletedAt } : note
        );
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  async deleteNote(noteId) {
    this.setLoading(true);
    try {
      await apiHandler.delete(`/api/device-notes/${noteId}`);
      runInAction(() => {
        this.notes = this.notes.filter((note) => note.id !== noteId);
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  async unarchiveNote(noteId) {
    this.setLoading(true);
    try {
      // Send the update to the backend (set deleted_at to null)
      await apiHandler.patch(`/api/device-notes/${noteId}/unarchive`, {
        deleted_at: null,
      });
      runInAction(() => {
        this.notes = this.notes.map((note) =>
          note.id === noteId ? { ...note, deleted_at: null } : note
        );
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(false);
    }
  }

  getNotesByDeviceId(device_id) {
    return this.notes.filter((note) => note.device_id === Number(device_id));
  }

  setLoading(loading) {
    this.loading = loading;
  }

  handleError(error) {
    runInAction(() => {
      this.error = error.message || "An unexpected error occurred";
    });
  }

  clearError() {
    this.error = null;
  }

  clearData() {
    runInAction(() => {
      this.notes = [];
    });
  }
}

const deviceNoteStore = new DeviceNoteStore();
export default deviceNoteStore;
